/**
 * 提醒偏好面板**端侧纯逻辑**（渲染输入 → 请求载荷）
 *
 * ## 🔴 本文件**不做任何口径判定**
 *
 * | 问题 | 归属 |
 * |---|---|
 * | 默认值是什么 | 🔴 **服务端**（无行 ⇒ 表默认值）—— 端侧**不写第二份默认值** |
 * | 「起止相同 ⇒ 非法」 | 🔴 **共享层内核** `assertDoNotDisturbRange()`（端侧只调它，不重写判定） |
 * | 该不该推 / 推什么 | **H 域**（本文件只负责"把用户点的东西变成请求体"） |
 *
 * 本文件只回答三件**渲染才需要**的事：① 草稿怎么建；② 有没有改动；③ 请求体长什么样。
 */

import {
  assertDoNotDisturbRange,
  type ReminderPreferenceView,
  type UpdatePreferencesRequest,
} from '@jiaying/shared';

/** 面板草稿（形状与契约 `ReminderPreferenceView` **一致** —— 少一层转换就少一处漂移） */
export type ReminderPreferenceDraft = ReminderPreferenceView;

/**
 * 用服务端下发的值建草稿。
 *
 * 🔴 **深拷贝 `typeEnabled`** —— 直接引用会让"改草稿"顺手改掉 react-query 的缓存对象，
 * 表现是"还没保存，页面别处已经变了"（且 `isDirty` 恒为 false ⇒ 保存按钮永远灰着）。
 */
export function draftFrom(view: ReminderPreferenceView): ReminderPreferenceDraft {
  return { ...view, typeEnabled: { ...view.typeEnabled } };
}

/**
 * 是否与已保存值不同（决定保存按钮可用性）。
 *
 * 🔴 **逐字段比**（不用 `JSON.stringify` —— 键序变化会被误判成"有改动"，反之亦然）。
 */
export function isDirty(saved: ReminderPreferenceView, draft: ReminderPreferenceDraft): boolean {
  if (saved.level !== draft.level) return true;
  if (saved.dndEnabled !== draft.dndEnabled) return true;
  if (saved.dndStart !== draft.dndStart) return true;
  if (saved.dndEnd !== draft.dndEnd) return true;
  const keys = Object.keys(saved.typeEnabled) as (keyof typeof saved.typeEnabled)[];
  return keys.some((k) => saved.typeEnabled[k] !== draft.typeEnabled[k]);
}

/**
 * 端侧**即时校验**（保存前跑，挡掉注定 400 的请求）。
 *
 * 🔴 判定**直接调共享层内核**（`assertDoNotDisturbRange`）—— 端侧**不重写**
 * 「起止相同 / 格式非法」这两条。返回 `null` = 通过；否则是要展示给用户的原因。
 */
export function validateDraft(draft: ReminderPreferenceDraft): string | null {
  try {
    assertDoNotDisturbRange(draft.dndStart, draft.dndEnd);
    return null;
  } catch (err: unknown) {
    return err instanceof Error ? err.message : String(err);
  }
}

/**
 * 草稿 → 请求体。
 *
 * 🔴 **`reminder` 组整体替换**（`contract/setting.ts` 文件头）：五个类型开关**一个都不能少** ——
 * 少传一个会被服务端契约判为非法（而不是"保持原值"）。
 */
export function toPatch(draft: ReminderPreferenceDraft): UpdatePreferencesRequest {
  return { reminder: { ...draft, typeEnabled: { ...draft.typeEnabled } } };
}

/** 翻转某个类型开关（返回**新对象** —— 面板的 `setState` 依赖引用变化） */
export function withTypeToggled(
  draft: ReminderPreferenceDraft,
  key: keyof ReminderPreferenceDraft['typeEnabled'],
  value: boolean,
): ReminderPreferenceDraft {
  return { ...draft, typeEnabled: { ...draft.typeEnabled, [key]: value } };
}
